import { useEffect, useState } from 'react';
import { Eye, Plus, Save, Upload, X } from 'lucide-react';
import { adviserApi, type AdviserProfileContent, type ProfileState } from '../../advisers/api';
import AdviserProfileView from '../../advisers/AdviserProfileView';
import { profileReviewLabels, profileReviewStage } from '../../advisers/profileReview';

export const blankAdviserProfile = (firmName = ''): AdviserProfileContent => ({
  firmName,
  headline: '',
  introduction: '',
  logoUrl: '',
  coverImageUrl: '',
  services: [],
  specialities: [],
  industries: [],
  location: '',
  serviceAreas: [],
  websiteUrl: '',
  linkedinUrl: '',
  contactEmail: '',
  contactPhone: '',
  bookingUrl: '',
  team: [],
});
const lists = ['services', 'specialities', 'industries', 'serviceAreas'] as const;
const labels: Record<string, string> = {
  firmName: 'Firm name',
  headline: 'Headline',
  introduction: 'Introduction',
  logoUrl: 'Logo image URL',
  coverImageUrl: 'Cover image URL',
  services: 'Services',
  specialities: 'Expertise',
  industries: 'Industries served',
  location: 'Location',
  serviceAreas: 'Service areas',
  websiteUrl: 'Website',
  linkedinUrl: 'LinkedIn',
  contactEmail: 'Public email',
  contactPhone: 'Public phone',
};

export default function AdviserProfileEditor({
  practiceId,
  practiceName,
  profile,
  onSaved,
}: {
  practiceId: string;
  practiceName: string;
  profile: ProfileState | null;
  onSaved: (profile: ProfileState) => void;
}) {
  const [content, setContent] = useState<AdviserProfileContent>(
    profile?.draft || blankAdviserProfile(practiceName)
  );
  const [slug, setSlug] = useState(
    profile?.slug ||
      practiceName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '')
  );
  const [listDrafts, setListDrafts] = useState<Record<string, string>>(() =>
    Object.fromEntries(lists.map((key) => [key, (profile?.draft[key] || []).join('\n')]))
  );
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [dirty, setDirty] = useState(false);
  useEffect(() => {
    setContent(profile?.draft || blankAdviserProfile(practiceName));
    setSlug(
      profile?.slug ||
        practiceName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, '')
    );
    setListDrafts(
      Object.fromEntries(lists.map((key) => [key, (profile?.draft[key] || []).join('\n')]))
    );
    setDirty(false);
  }, [practiceId, profile, practiceName]);
  const update = <K extends keyof AdviserProfileContent>(
    key: K,
    value: AdviserProfileContent[K]
  ) => {
    setContent((current) => ({ ...current, [key]: value }));
    setDirty(true);
    setNotice('');
  };
  const act = async (action: () => Promise<ProfileState>, message: string) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const saved = await action();
      onSaved(saved);
      setDirty(false);
      setNotice(message);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unable to update profile.');
    } finally {
      setBusy(false);
    }
  };
  const upload = async (file: File | undefined, apply: (url: string) => void) => {
    if (!file) return;
    if (
      !['image/jpeg', 'image/png', 'image/webp'].includes(file.type) ||
      file.size > 8 * 1024 * 1024
    ) {
      setError('Choose a JPG, PNG or WebP image smaller than 8 MB.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const result = await adviserApi.upload(practiceId, file);
      apply(result.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Image upload failed.');
    } finally {
      setBusy(false);
    }
  };
  const approved = profile?.approvalStatus === 'approved';
  const reviewStage = profileReviewStage(profile);
  return (
    <div>
      <div className="adviser-section-heading">
        <div>
          <h2>Your public firm profile</h2>
          <p>Introduce your firm and team. Draft edits stay private until you publish.</p>
        </div>
        <button
          className="adviser-button adviser-button-secondary"
          type="button"
          onClick={() => setPreview(!preview)}
        >
          <Eye size={17} />
          {preview ? 'Back to editor' : 'Preview'}
        </button>
      </div>
      <div className="adviser-notice">
        <strong>{profileReviewLabels[reviewStage]}</strong>
        <p>
          {approved
            ? profile?.published
              ? `Published at /advisers/${profile.slug}. You can publish updates whenever you are ready.`
              : 'Your profile is approved. Publish it when you are ready.'
            : reviewStage === 'changes_requested'
              ? 'Your initial profile needs changes. Update and save your draft for another review.'
              : reviewStage === 'submitted'
                ? 'Your initial profile has been submitted for Onsavii review. You can update the draft while you wait.'
                : 'Onsavii reviews your initial profile. Save a complete draft to add it to the review queue.'}
        </p>
      </div>
      {profile?.reviewReason && !approved && (
        <p className="adviser-notice">Review feedback: {profile.reviewReason}</p>
      )}
      {error && (
        <p className="adviser-error" role="alert">
          {error}
        </p>
      )}
      {notice && (
        <p className="adviser-success" role="status">
          {notice}
        </p>
      )}
      {preview ? (
        <AdviserProfileView profile={content} preview />
      ) : (
        <form
          className="adviser-panel adviser-profile-form"
          onSubmit={(event) => {
            event.preventDefault();
            void act(
              () => adviserApi.saveProfile(practiceId, content, slug),
              approved
                ? 'Draft saved. Publish to update your public profile.'
                : 'Draft saved for initial profile review.'
            );
          }}
        >
          <fieldset disabled={busy}>
            <legend>Firm identity</legend>
            <div className="adviser-form-grid">
              <label>
                Firm name
                <input
                  required
                  maxLength={200}
                  value={content.firmName}
                  onChange={(event) => update('firmName', event.target.value)}
                />
              </label>
              <label>
                Public profile URL
                <div className="adviser-slug">
                  <span>/advisers/</span>
                  <input
                    required
                    minLength={3}
                    maxLength={80}
                    pattern="[a-z0-9]+(-[a-z0-9]+)*"
                    value={slug}
                    disabled={Boolean(profile?.publishedAt)}
                    onChange={(event) => {
                      setSlug(event.target.value);
                      setDirty(true);
                    }}
                  />
                </div>
                <small>
                  {profile?.publishedAt
                    ? 'Your published URL is permanent.'
                    : 'Lowercase letters, numbers and hyphens.'}
                </small>
              </label>
              <label className="adviser-form-full">
                Headline
                <input
                  required
                  maxLength={200}
                  value={content.headline}
                  onChange={(event) => update('headline', event.target.value)}
                  placeholder="What you help businesses achieve"
                />
              </label>
              <label className="adviser-form-full">
                Introduction
                <textarea
                  required
                  rows={6}
                  maxLength={6000}
                  value={content.introduction}
                  onChange={(event) => update('introduction', event.target.value)}
                />
              </label>
              {(['logoUrl', 'coverImageUrl'] as const).map((key) => (
                <div key={key}>
                  <label>
                    {labels[key]}
                    <input
                      type="url"
                      value={content[key]}
                      readOnly
                      placeholder="Upload an image to your practice"
                    />
                  </label>
                  <button
                    className="adviser-button adviser-button-quiet"
                    type="button"
                    disabled={!content[key]}
                    onClick={() => update(key, '')}
                  >
                    Clear image
                  </button>
                  <label className="adviser-upload">
                    <Upload size={15} />
                    Upload {key === 'logoUrl' ? 'logo' : 'cover'}
                    <input
                      aria-label={`Upload ${key === 'logoUrl' ? 'logo' : 'cover'}`}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(event) => {
                        void upload(event.target.files?.[0], (url) => update(key, url));
                        event.target.value = '';
                      }}
                    />
                  </label>
                </div>
              ))}
            </div>
          </fieldset>
          <fieldset disabled={busy}>
            <legend>Services and reach</legend>
            <p className="adviser-muted">
              One entry per line. These fields help clients find your firm.
            </p>
            <div className="adviser-form-grid">
              {lists.map((key) => (
                <label key={key}>
                  {labels[key]}
                  <textarea
                    rows={3}
                    value={listDrafts[key]}
                    onChange={(event) => {
                      setListDrafts((current) => ({ ...current, [key]: event.target.value }));
                      update(
                        key,
                        event.target.value
                          .split('\n')
                          .map((value) => value.trim())
                          .filter(Boolean)
                      );
                    }}
                  />
                </label>
              ))}
              <label>
                Location
                <input
                  value={content.location}
                  maxLength={200}
                  onChange={(event) => update('location', event.target.value)}
                />
              </label>
            </div>
          </fieldset>
          <fieldset disabled={busy}>
            <legend>Public contact</legend>
            <div className="adviser-form-grid">
              {(['websiteUrl', 'linkedinUrl', 'contactEmail', 'contactPhone'] as const).map(
                (key) => (
                  <label key={key}>
                    {labels[key]}
                    <input
                      type={
                        key === 'contactEmail' ? 'email' : key === 'contactPhone' ? 'tel' : 'url'
                      }
                      value={content[key]}
                      onChange={(event) => update(key, event.target.value)}
                      placeholder={key.endsWith('Url') ? 'https://…' : ''}
                    />
                  </label>
                )
              )}
            </div>
            <p className="adviser-muted">
              Include at least one contact option. Only information entered here becomes public.
            </p>
            <p className="adviser-notice">
              Conversation requests go through StratPlan. Practice owners receive an email and a
              notification, and can respond from the Enquiries tab.
            </p>
          </fieldset>
          <fieldset disabled={busy}>
            <legend>Your public team</legend>
            <p className="adviser-muted">
              Add people who have agreed to appear on your firm's public page. These entries are
              independent of private practice memberships.
            </p>
            {content.team.map((member, index) => (
              <div className="adviser-team-editor" key={member.id || index}>
                <div className="adviser-form-grid">
                  {(['name', 'role', 'biography', 'photoUrl'] as const).map((key) => (
                    <label key={key}>
                      {key === 'photoUrl'
                        ? 'Photo URL'
                        : key === 'biography'
                          ? 'Biography'
                          : key === 'role'
                            ? 'Public role'
                            : 'Name'}
                      {key === 'biography' ? (
                        <textarea
                          rows={3}
                          maxLength={2000}
                          value={member[key]}
                          onChange={(event) =>
                            update(
                              'team',
                              content.team.map((person, position) =>
                                position === index
                                  ? { ...person, [key]: event.target.value }
                                  : person
                              )
                            )
                          }
                        />
                      ) : (
                        <input
                          type={key === 'photoUrl' ? 'url' : 'text'}
                          readOnly={key === 'photoUrl'}
                          value={member[key] || ''}
                          maxLength={key === 'photoUrl' ? 2048 : 200}
                          onChange={(event) =>
                            update(
                              'team',
                              content.team.map((person, position) =>
                                position === index
                                  ? { ...person, [key]: event.target.value }
                                  : person
                              )
                            )
                          }
                        />
                      )}
                    </label>
                  ))}
                </div>
                <div className="adviser-actions">
                  <label className="adviser-upload">
                    <Upload size={15} />
                    Upload photo
                    <input
                      aria-label={`Upload photo for ${member.name || `team member ${index + 1}`}`}
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      onChange={(event) => {
                        void upload(event.target.files?.[0], (url) =>
                          update(
                            'team',
                            content.team.map((person, position) =>
                              position === index ? { ...person, photoUrl: url } : person
                            )
                          )
                        );
                        event.target.value = '';
                      }}
                    />
                  </label>
                  <button
                    type="button"
                    className="adviser-button adviser-button-quiet"
                    onClick={() =>
                      update(
                        'team',
                        content.team.filter((_, position) => position !== index)
                      )
                    }
                  >
                    <X size={15} />
                    Remove team member
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              disabled={content.team.length >= 40}
              className="adviser-button adviser-button-secondary"
              onClick={() =>
                update('team', [
                  ...content.team,
                  { id: crypto.randomUUID(), name: '', role: '', biography: '', photoUrl: '' },
                ])
              }
            >
              <Plus size={16} />
              Add team member
            </button>
          </fieldset>
          <div className="adviser-editor-actions">
            <button className="adviser-button" type="submit" disabled={busy}>
              <Save size={17} />
              {busy ? 'Saving…' : approved ? 'Save draft' : 'Save for review'}
            </button>
            {approved && (
              <button
                className="adviser-button adviser-button-secondary"
                type="button"
                disabled={busy || dirty}
                onClick={() =>
                  void act(
                    () => adviserApi.publishProfile(practiceId),
                    'Your profile is now published across the directory and homepage.'
                  )
                }
              >
                Publish saved draft
              </button>
            )}
            {profile?.published && (
              <button
                className="adviser-button adviser-button-quiet"
                type="button"
                disabled={busy}
                onClick={() =>
                  void act(
                    () => adviserApi.unpublishProfile(practiceId),
                    'Profile unpublished. Your draft is preserved.'
                  )
                }
              >
                Unpublish
              </button>
            )}
            {dirty && approved && (
              <span className="adviser-muted">Save changes before publishing.</span>
            )}
          </div>
        </form>
      )}
    </div>
  );
}
